import { DragEvent, DragEventHandler, useCallback, useEffect, useMemo, useRef } from 'react';

interface UseNativeDropZoneOptions<TElement extends HTMLElement> {
    active: boolean;
    canDrop: (event: DragEvent<TElement>) => boolean;
    disabled?: boolean;
    onActiveChange: (active: boolean) => void;
    onDrop: (event: DragEvent<TElement>) => void;
    stopPropagation?: boolean;
}

export interface NativeDropZoneHandlers<TElement extends HTMLElement> {
    'data-platform-drop-zone'?: 'active';
    onDragEnter: DragEventHandler<TElement>;
    onDragLeave: DragEventHandler<TElement>;
    onDragOver: DragEventHandler<TElement>;
    onDrop: DragEventHandler<TElement>;
}

/**
 * Normalizes native HTML drop-zone behavior with the same lifecycle contract as
 * the platform's managed drag engine. In particular, targets are cleared when
 * the pointer actually leaves their boundary instead of remaining visually
 * active until the drag ends.
 */
const useNativeDropZone = <TElement extends HTMLElement>({
    active,
    canDrop,
    disabled = false,
    onActiveChange,
    onDrop,
    stopPropagation = true,
}: UseNativeDropZoneOptions<TElement>): NativeDropZoneHandlers<TElement> => {
    const activeRef = useRef(active);

    useEffect(() => {
        activeRef.current = active;

        if (disabled && active) {
            activeRef.current = false;
            onActiveChange(false);
        }
    }, [active, disabled, onActiveChange]);

    const claimDropTarget = useCallback(
        (event: DragEvent<TElement>) => {
            if (stopPropagation) {
                event.stopPropagation();
            }

            const eligible = !disabled && canDrop(event);

            if (!eligible) {
                if (activeRef.current) {
                    activeRef.current = false;
                    onActiveChange(false);
                }

                return false;
            }

            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            if (!activeRef.current) {
                activeRef.current = true;
                onActiveChange(true);
            }

            return true;
        },
        [canDrop, disabled, onActiveChange, stopPropagation],
    );

    const handleDragLeave = useCallback(
        (event: DragEvent<TElement>) => {
            if (stopPropagation) {
                event.stopPropagation();
            }

            const nextTarget = event.relatedTarget;

            if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
                return;
            }

            if (activeRef.current) {
                activeRef.current = false;
                onActiveChange(false);
            }
        },
        [onActiveChange, stopPropagation],
    );

    const handleDrop = useCallback(
        (event: DragEvent<TElement>) => {
            if (stopPropagation) {
                event.stopPropagation();
            }

            const eligible = !disabled && canDrop(event);

            if (activeRef.current) {
                activeRef.current = false;
                onActiveChange(false);
            }

            if (!eligible) {
                return;
            }

            event.preventDefault();
            onDrop(event);
        },
        [canDrop, disabled, onActiveChange, onDrop, stopPropagation],
    );

    return useMemo(
        () => ({
            'data-platform-drop-zone': active ? ('active' as const) : undefined,
            onDragEnter: claimDropTarget,
            onDragLeave: handleDragLeave,
            onDragOver: claimDropTarget,
            onDrop: handleDrop,
        }),
        [active, claimDropTarget, handleDragLeave, handleDrop],
    );
};

export default useNativeDropZone;
